<template>
    <div class="mcp-documentation">
        <div class="container-search">
            <div class="left">
                <a class="back-link" @click="$router.push('/dash/settings/api_keys')">
                    <i class="fa-solid fa-arrow-left"></i>
                    <span>{{ $t('dash.settings.mcp_docs.back') }}</span>
                </a>
                <h3 class="title">{{ $t('dash.settings.mcp_docs.title') }}<BetaBadge></BetaBadge></h3>
                <p class="subtitle">{{ $t('dash.settings.mcp_docs.subtitle') }}</p>
            </div>
        </div>

        <div class="section-nav">
            <a @click="scrollTo('connect')">{{ $t('dash.settings.mcp_docs.nav.connect') }}</a>
            <a @click="scrollTo('reference')">{{ $t('dash.settings.mcp_docs.nav.reference') }}</a>
        </div>

        <section id="mcp-docs-connect" class="connect-section">
            <h4 class="section-title">{{ $t('dash.settings.mcp_docs.connect.title') }}</h4>

            <div class="client-tabs">
                <div v-for="client in clients" :key="client.key" :class="{ 'client-tab': true, active: activeClient === client.key }" @click="activeClient = client.key">
                    <i :class="client.icon"></i>
                    <span>{{ client.label }}</span>
                </div>
            </div>

            <div class="client-steps">
                <ol>
                    <li v-for="(step, index) in steps" :key="index" v-html="step"></li>
                </ol>
            </div>

            <div class="endpoint-field">
                <span class="label">{{ $t('dash.settings.mcp_docs.connect.endpoint_label') }}</span>
                <div class="api-url-field">
                    <input type="text" readonly :value="mcpEndpoint" @click="$event.target.select()">
                    <button class="api-url-copy" @click="copyEndpoint" :title="$t('dash.settings.mcp_docs.connect.endpoint_copy')">
                        <i :class="endpointCopied ? 'fa-solid fa-check' : 'fa-solid fa-copy'"></i>
                    </button>
                </div>
            </div>
        </section>

        <section id="mcp-docs-reference" class="reference-section">
            <mcp-documentation-reference></mcp-documentation-reference>
        </section>
    </div>
</template>

<script>
import mcpDocumentationReference from './mcp-documentation-reference.vue'
import BetaBadge from '../utils/BetaBadge.vue'

const { VUE_APP_API_HOST } = process.env

export default {
    name: 'mcp-documentation',

    components: {
        'mcp-documentation-reference': mcpDocumentationReference,
        BetaBadge
    },

    data() {
        return {
            activeClient: 'claude',
            endpointCopied: false
        }
    },

    computed: {
        mcpEndpoint() {
            return `${VUE_APP_API_HOST}/mcp`
        },

        clients() {
            return [
                { key: 'claude', label: 'Claude', icon: 'fa-solid fa-comment-dots' },
                { key: 'chatgpt', label: 'ChatGPT', icon: 'fa-solid fa-comment-dots' },
                { key: 'other', label: this.$t('dash.settings.mcp_docs.connect.client_other'), icon: 'fa-solid fa-plug' }
            ]
        },

        steps() {
            const key = `dash.settings.mcp_docs.connect.steps.${this.activeClient}`
            // Each client's steps are stored as one translated string per step, numbered
            // 1..6 in the locale files (dash.settings.mcp_docs.connect.steps.<client>.<n>) -
            // stop at the first missing key rather than assuming a fixed count.
            const result = []
            for (let i = 1; i <= 6; i++) {
                const stepKey = `${key}.${i}`
                const text = this.$t(stepKey)
                if (text === stepKey) break // vue-i18n returns the key itself when missing
                result.push(text)
            }
            return result
        }
    },

    methods: {
        scrollTo(section) {
            const el = document.getElementById(`mcp-docs-${section}`)
            if (el) el.scrollIntoView({ behavior: 'smooth' })
        },

        copyEndpoint() {
            const el = document.createElement('textarea')
            el.value = this.mcpEndpoint
            el.setAttribute('readonly', '')
            el.style.position = 'absolute'
            el.style.left = '-9999px'
            document.body.appendChild(el)
            const selected = document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false
            el.select()
            document.execCommand('copy')
            document.body.removeChild(el)
            if (selected) {
                document.getSelection().removeAllRanges()
                document.getSelection().addRange(selected)
            }
            this.endpointCopied = true
            setTimeout(() => { this.endpointCopied = false }, 2000)
        }
    }
}
</script>

<style lang="scss" scoped>
.mcp-documentation {
    .container-search {
        margin-bottom: 20px;

        .back-link {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            color: #6B1F4D;
            font-size: 12.5px;
            font-weight: 600;
            cursor: pointer;
            margin-bottom: 10px;

            &:hover { text-decoration: underline; }
        }

        .title {
            font-weight: 600;
            font-size: 16px;
            margin: 0 0 4px;
        }

        .subtitle {
            color: #9ba3b7;
            font-size: 13px;
            margin: 0;
        }
    }

    .section-nav {
        display: flex;
        gap: 20px;
        border-bottom: 1px solid #e3e6ee;
        margin-bottom: 24px;
        padding-bottom: 12px;

        a {
            color: #555e75;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;

            &:hover { color: #6B1F4D; }
        }
    }

    .section-title {
        font-weight: 600;
        font-size: 15px;
        margin: 0 0 14px;
    }

    .connect-section {
        background: #faf8fc;
        border: 1px solid #F1EDF5;
        border-radius: 10px;
        padding: 20px;
        margin-bottom: 32px;

        .client-tabs {
            display: flex;
            gap: 8px;
            margin-bottom: 16px;

            .client-tab {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 8px 14px;
                border-radius: 6px;
                background: #fff;
                border: 1px solid #e3e6ee;
                font-size: 12.5px;
                font-weight: 600;
                color: #555e75;
                cursor: pointer;

                &.active {
                    background: #6B1F4D;
                    border-color: #6B1F4D;
                    color: #fff;
                }
            }
        }

        .client-steps {
            ol {
                margin: 0 0 16px;
                padding-left: 20px;

                li {
                    font-size: 13px;
                    color: #333;
                    margin-bottom: 8px;
                    line-height: 1.5;

                    ::v-deep code {
                        background: #F1EDF5;
                        padding: 1px 5px;
                        border-radius: 4px;
                        font-size: 12px;
                    }
                }
            }
        }

        .endpoint-field {
            .label {
                display: block;
                font-size: 12px;
                color: #9ba3b7;
                margin-bottom: 6px;
            }

            .api-url-field {
                display: flex;
                align-items: center;
                background: #fff;
                border: 1px solid #e3e6ee;
                border-radius: 6px;
                overflow: hidden;
                max-width: 480px;

                input {
                    flex: 1;
                    border: none;
                    padding: 8px 10px;
                    font-size: 12.5px;
                    color: #555e75;
                    background: transparent;
                }

                .api-url-copy {
                    border: none;
                    background: transparent;
                    padding: 8px 12px;
                    cursor: pointer;
                    color: #6B1F4D;
                }
            }
        }
    }
}
</style>
